import { Paper } from '@material-ui/core';
import { Info } from '@material-ui/icons';
import { makeStyles } from '@material-ui/styles';
import { StyleBreakpoints } from '@utils/constants';
import { useInjectReducer } from '@utils/injectReducer';
import { useInjectSaga } from '@utils/injectSaga';
import MaterialTable from 'material-table';
import { default as React, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { fetchReviewsStatistic as fetchReviewsAPI } from '../../../api/taptotip';
import TopNav from '../../../components/Nav/TopNav';
import TipDirectLeftMenu from '../../../components/Nav/TopNav/TipDirectLeftMenu';
import localizationMessages from '../../../components/Resources/ResourcesTable/messages';
import TotalStars from '../../../components/TapToTip/TotalStars';
import Filters from './Filters';
import * as actions from './actions';
import messages from './messages';
import reducer from './reducer';
import saga from './saga';
import { date2str } from '@utils/helpers';

const useStyles = makeStyles(theme => ({
  outerContainer: {
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.up(StyleBreakpoints.sm)]: {
      flexDirection: 'row'
    }
  },
  container: {
    padding: '24px',
    display: 'flex',
    justifyContent: 'flex-start',
    width: '80%',
    maxHeight: '90vh',
    flexDirection: 'column',
    overflowY: 'auto',

    [theme.breakpoints.down(StyleBreakpoints.sm)]: {
      width: '100%',
      maxWidth: '100%',
      flexWrap: 'noWrap',
      padding: 'unset'
    }
  },
}));

const TippingReviews = ({
  agentsProfiles,
  fetchAgentsProfiles,
  reviewsStatistic,
  fetchReviewsStatistic,
  user,
  isLoading
}) => {
  const intl = useIntl();
  const classes = useStyles();
  useInjectReducer({ key: 'tipReviews', reducer });
  useInjectSaga({ key: 'tipReviews', saga });

  const [selectedTours, setSelectedTours] = useState([]);
  const [selectedProfiles, setSelectedProfiles] = useState([]);
  const [dateRange, setDateRange] = useState({
    start: new Date().setMonth(new Date().getMonth() - 12),
    end: new Date().getTime()
  });
  const [groupBy, setGroupBy] = useState('tour');
  const [topTour, setTopTour] = useState(null);
  const [topGuide, setTopGuide] = useState(null);

  const filteredAgentsProfiles = useMemo(() => {
    return agentsProfiles?.filter(p => p.status == 'active');
  }, [agentsProfiles]);

  const tours = useMemo(() => {
    if (!filteredAgentsProfiles || !filteredAgentsProfiles.length) {
      return [];
    }

    const tempTours = filteredAgentsProfiles.reduce((acc, agent) => {
      if (agent.tours && agent.tours.length) {
        agent.tours.forEach(tour => {
          acc.set(tour._id, tour);
        });
      }
      return acc;
    }, new Map());

    return Array.from(tempTours.values());
  }, [filteredAgentsProfiles]);

  useEffect(() => {
    if (!agentsProfiles?.length) {
      fetchAgentsProfiles();
    }
  }, []);

  useEffect(() => {
    fetchReviewsStatistic({
      dateRange,
      tourIds: selectedTours,
      guideIds: selectedProfiles,
      groupBy,
      submitReviewOptionsClicked: {
        Google: true,
        TripAdvisor: true,
        Yelp: true,
        Email: true,
        Local: true
      }
    });

    const payload = {
      dateRange: { start: date2str(dateRange.start), end: date2str(dateRange.end) },
      tourIds: selectedTours,
      guideIds: selectedProfiles,
      submitReviewOptionsClicked: { Google: true, TripAdvisor: true, Yelp: true, Email: true, Local: true }
    };

    fetchReviewsAPI({ ...payload, groupBy: 'tour' }).then(res => {
      if (res && res.length) {
        const sorted = [...res].sort((a, b) => b.totalReviewsAmount - a.totalReviewsAmount);
        setTopTour(sorted[0]);
      } else {
        setTopTour(null);
      }
    });

    fetchReviewsAPI({ ...payload, groupBy: 'guide' }).then(res => {
      if (res && res.length) {
        const sorted = [...res].sort((a, b) => b.totalReviewsAmount - a.totalReviewsAmount);
        setTopGuide(sorted[0]);
      } else {
        setTopGuide(null);
      }
    });

  }, [selectedTours, selectedProfiles, dateRange, groupBy]);

  const displayStats = reviewsStatistic || [];

  const handleDownloadCSV = () => {
    if (!displayStats || !displayStats.length) return;

    const headers = [
      groupBy === 'tour' ? 'Tour Name' : 'User Name',
      'Avg Rating',
      '5★',
      '4★',
      '3★',
      '2★',
      '1★',
      'Total'
    ].join(',');

    const rows = displayStats.map(row => {
      return [
        `"${(row.title || row.tourTitle || row.guideName || 'Unknown').replace(/"/g, '""')}"`,
        row.averageRating,
        row.reviewsAmountByRating['5'] || 0,
        row.reviewsAmountByRating['4'] || 0,
        row.reviewsAmountByRating['3'] || 0,
        row.reviewsAmountByRating['2'] || 0,
        row.reviewsAmountByRating['1'] || 0,
        row.totalReviewsAmount || 0
      ].join(',');
    });

    const csvContent = "data:text/csv;charset=utf-8," + [headers, ...rows].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `reviews_breakdown_${groupBy}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  function getToolbarComponent() {
    return (
      <Filters
        tours={tours}
        profiles={filteredAgentsProfiles}
        selectedTours={selectedTours}
        setSelectedTours={setSelectedTours}
        selectedProfiles={selectedProfiles}
        setSelectedProfiles={setSelectedProfiles}
        dateRange={dateRange}
        setDateRange={setDateRange}
        userRole={user.role}
        groupBy={groupBy}
        setGroupBy={setGroupBy}
        onExportCSV={handleDownloadCSV}
      />
    );
  }

  const displayTopTour = topTour || null;
  const displayTopGuide = topGuide || null;

  return (
    <>
      <TopNav />
      <div className={classes.outerContainer}>
        <TipDirectLeftMenu selectedTab={'Reviews'} />
        <div className="flex-1 flex flex-col p-6 md:p-8 w-full max-w-7xl mx-auto min-w-0">
          <div className="mb-8 shrink-0">
            <h1 className="text-3xl font-bold text-slate-800 tracking-tight">Review Insights</h1>
            <p className="text-slate-500 mt-2">Gain insights into your top performing tours and team members.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 shrink-0">
            <div className="bg-gradient-to-br from-indigo-500 to-purple-600 grain rounded-3xl p-6 text-white shadow-xl shadow-indigo-200 relative overflow-hidden group">
              <div className="absolute -right-10 -top-10 w-40 h-40 bg-white/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700"></div>
              <h3 className="text-indigo-100 font-medium text-sm tracking-wider uppercase mb-1">Top Performing Tour</h3>
              {displayTopTour ? (
                <>
                  <div className="text-2xl font-bold mb-4 line-clamp-1">{displayTopTour.title || displayTopTour.tourTitle || displayTopTour.guideName || 'Unknown Tour'}</div>
                  <div className="flex items-center gap-4">
                    <div className="bg-white/20 px-4 py-2 rounded-xl backdrop-blur-md">
                      <span className="text-3xl font-bold">{displayTopTour.averageRating.toFixed(1)}</span>
                      <span className="text-indigo-100 ml-1 text-sm">avg rating</span>
                    </div>
                    <div className="bg-white/20 px-4 py-2 rounded-xl backdrop-blur-md">
                      <span className="text-3xl font-bold">{displayTopTour.totalReviewsAmount}</span>
                      <span className="text-indigo-100 ml-1 text-sm">reviews</span>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-lg font-medium text-indigo-100/75 mt-4">No reviews found for this date range</div>
              )}
            </div>

            <div className="bg-gradient-to-br from-emerald-400 to-teal-600 grain rounded-3xl p-6 text-white shadow-xl shadow-emerald-200 relative overflow-hidden group">
              <div className="absolute -right-10 -top-10 w-40 h-40 bg-white/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700"></div>
              <h3 className="text-emerald-100 font-medium text-sm tracking-wider uppercase mb-1">Top Performer</h3>
              {displayTopGuide ? (
                <>
                  <div className="text-2xl font-bold mb-4 line-clamp-1">{displayTopGuide.title || displayTopGuide.guideName || displayTopGuide.tourTitle || 'Unknown User'}</div>
                  <div className="flex items-center gap-4">
                    <div className="bg-white/20 px-4 py-2 rounded-xl backdrop-blur-md">
                      <span className="text-3xl font-bold">{displayTopGuide.averageRating.toFixed(1)}</span>
                      <span className="text-emerald-100 ml-1 text-sm">avg rating</span>
                    </div>
                    <div className="bg-white/20 px-4 py-2 rounded-xl backdrop-blur-md">
                      <span className="text-3xl font-bold">{displayTopGuide.totalReviewsAmount}</span>
                      <span className="text-emerald-100 ml-1 text-sm">reviews</span>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-lg font-medium text-emerald-100/75 mt-4">No reviews found for this date range</div>
              )}
            </div>
          </div>

          <div className="bg-slate-50 rounded-3xl shadow-sm border border-solid border-slate-200 p-6 mb-8 shrink-0">
            {getToolbarComponent()}
          </div>

          <div className="bg-slate-50 rounded-3xl shadow-sm border border-solid border-slate-200 mb-8 overflow-hidden shrink-0">
            <div className="p-6 border-0 border-b border-solid border-slate-200 bg-slate-100/50 flex justify-between items-center">
              <h2 className="text-xl font-bold text-slate-800">Reviews Breakdown</h2>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-100/50 border-0 border-b border-solid border-slate-200 text-slate-600 text-xs font-bold uppercase tracking-wider">
                    <th className="px-6 py-4">{groupBy === 'tour' ? 'Tour Name' : 'User Name'}</th>
                    <th className="px-6 py-4">Avg Rating</th>
                    <th className="px-6 py-4 text-center">5★</th>
                    <th className="px-6 py-4 text-center">4★</th>
                    <th className="px-6 py-4 text-center">3★</th>
                    <th className="px-6 py-4 text-center">2★</th>
                    <th className="px-6 py-4 text-center">1★</th>
                    <th className="px-6 py-4 text-right">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-solid divide-slate-100 bg-white">
                  {displayStats && displayStats.length > 0 ? (
                    displayStats.map((row, i) => (
                      <tr key={i} className="hover:bg-slate-50/75 transition-colors duration-150">
                        <td className="px-6 py-4 font-semibold text-slate-800">{row.title || row.tourTitle || row.guideName || 'Unknown'}</td>
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 px-2.5 py-1 rounded-md text-xs font-bold border border-solid border-amber-200/55">
                            {row.averageRating.toFixed(1)} <span className="text-amber-400">★</span>
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center text-slate-600 font-medium">{row.reviewsAmountByRating['5'] || 0}</td>
                        <td className="px-6 py-4 text-center text-slate-600 font-medium">{row.reviewsAmountByRating['4'] || 0}</td>
                        <td className="px-6 py-4 text-center text-slate-600 font-medium">{row.reviewsAmountByRating['3'] || 0}</td>
                        <td className="px-6 py-4 text-center text-slate-600 font-medium">{row.reviewsAmountByRating['2'] || 0}</td>
                        <td className="px-6 py-4 text-center text-slate-600 font-medium">{row.reviewsAmountByRating['1'] || 0}</td>
                        <td className="px-6 py-4 text-right font-bold text-slate-900">{row.totalReviewsAmount || 0}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" className="p-8 text-center text-slate-400">
                        No reviews found for the selected criteria.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex flex-col xl:flex-row gap-6 items-stretch shrink-0">
            <div className="flex-1 w-full bg-slate-50 p-6 rounded-3xl shadow-sm border border-solid border-slate-200 flex flex-col">
              <TotalStars reviews={displayStats} />
            </div>

            <div className="w-full xl:max-w-sm shrink-0 bg-gradient-to-br from-amber-400 to-orange-500 grain rounded-3xl p-6 text-white shadow-xl shadow-amber-200 relative overflow-hidden group flex flex-col">
              <div className="absolute -right-10 -top-10 w-40 h-40 bg-white/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700"></div>
              <div className="flex gap-4 items-start relative z-10">
                <div className="bg-white/20 p-3 rounded-full text-white shrink-0 backdrop-blur-md">
                  <Info />
                </div>
                <div>
                  <h4 className="font-bold text-white mb-1 text-lg">Note on Review Data</h4>
                  <p className="text-orange-50 text-sm leading-relaxed font-medium">
                    Figures represent the number of customers which have opened your review link only. Ratings are calculated based on these interactions.
                  </p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

const mapStateToProps = state => ({
  agentsProfiles: state.tipReviews?.agentsProfiles,
  reviewsStatistic: state.tipReviews?.reviewsStatistic,
  isLoading: state.tipReviews?.isLoading,
  user: state.user.user
});

const mapDispatchToProps = dispatch => ({
  fetchAgentsProfiles: () => dispatch(actions.fetchAgentsProfiles()),
  fetchReviewsStatistic: payload => dispatch(actions.fetchReviewsStatistic(payload))
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(TippingReviews);
